import { InputBaseComponentProps } from '@material-ui/core';
import { FC, FunctionComponent, useState, useEffect } from 'react';
import MaskedField from 'react-masked-field';
import ObscurableField from 'ui-kit/inputs/ObscurableField';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import {
  SSN_ITIN_REGEX,
  EIN_REGEX,
  FOREIGN_TAX_ID_REGEX,
  FOREIGN_PASSPORT_REGEX,
  FOREIGN_ENTITY_REGISTRATION_REGEX,
} from 'utilities/constants/validation-patterns';

interface IdentificationNumberFieldProps {
  identificationType: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  label?: string;
  name?: string;
  'data-testid'?: string;
  'aria-label'?: string;
  keyPrefix?: string; // Used to generate unique keys for ObscurableField
}

export const IdentificationNumberField: FC<IdentificationNumberFieldProps> = ({
  identificationType,
  value,
  onChange,
  onBlur,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  label = 'Unique Identifying Number',
  name = 'identificationNumber',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  keyPrefix = 'identification',
}) => {
  const [internalError, setInternalError] = useState<string | undefined>();
  const [touched, setTouched] = useState(false);

  // Clear internal error when value becomes valid
  useEffect(() => {
    if (touched && value) {
      const trimmedValue = value.trim();
      if (identificationType === 'ssn-itin' && SSN_ITIN_REGEX.test(trimmedValue)) {
        setInternalError(undefined);
      } else if (identificationType === 'ein' && EIN_REGEX.test(trimmedValue)) {
        setInternalError(undefined);
      } else if (identificationType === 'foreignTaxId' && FOREIGN_TAX_ID_REGEX.test(trimmedValue)) {
        setInternalError(undefined);
      } else if (identificationType === 'foreignPassport' && FOREIGN_PASSPORT_REGEX.test(trimmedValue)) {
        setInternalError(undefined);
      } else if (
        identificationType === 'foreignEntityRegistrationNumber' &&
        FOREIGN_ENTITY_REGISTRATION_REGEX.test(trimmedValue)
      ) {
        setInternalError(undefined);
      }
    }
  }, [value, identificationType, touched]);

  // Helper function to validate SSN/ITIN/EIN and foreign ID formats
  const validateFormat = (inputValue: string): string | undefined => {
    const trimmedValue = inputValue.trim();

    // If field is empty, don't show format error (required validation handles this)
    if (!trimmedValue) {
      return undefined;
    }

    if (identificationType === 'ssn-itin') {
      if (!SSN_ITIN_REGEX.test(trimmedValue)) {
        return 'SSN/ITIN must be in XXX-XX-XXXX format (e.g., 123-45-6789)';
      }
    } else if (identificationType === 'ein') {
      if (!EIN_REGEX.test(trimmedValue)) {
        return 'EIN must be in XX-XXXXXXX format (e.g., 12-3456789)';
      }
    } else if (identificationType === 'foreignTaxId') {
      if (!FOREIGN_TAX_ID_REGEX.test(trimmedValue)) {
        return 'Foreign Tax ID must be 6-20 alphanumeric characters';
      }
    } else if (identificationType === 'foreignPassport') {
      if (!FOREIGN_PASSPORT_REGEX.test(trimmedValue)) {
        return 'Foreign Passport must be 6-9 alphanumeric characters';
      }
    } else if (identificationType === 'foreignEntityRegistrationNumber') {
      if (!FOREIGN_ENTITY_REGISTRATION_REGEX.test(trimmedValue)) {
        return 'Foreign Entity Registration Number must be 5-20 alphanumeric characters';
      }
    }

    return undefined;
  };

  // Handle blur event for immediate validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    const validationError = validateFormat(e.target.value);
    setInternalError(validationError);

    if (onBlur) {
      onBlur(e);
    }
  };

  // Helper function to filter alphanumeric characters only
  const handleAlphanumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filteredValue = e.target.value.replace(/[^A-Za-z0-9]/g, '');
    onChange({
      ...e,
      target: {
        ...e.target,
        value: filteredValue,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  // Determine which error to show (external error from parent takes precedence)
  const displayError = error || !!internalError;
  const displayHelperText = helperText || internalError;

  if (identificationType === 'ssn-itin') {
    return (
      <ObscurableField
        key={`ssn-itin-${keyPrefix}`}
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        error={displayError}
        helperText={displayHelperText}
        obscurePattern={/\d/g}
        obscureReplacer={(match, offset) => {
          if (typeof offset === 'number' && offset < 6) {
            return '•';
          }
          return match;
        }}
        InputProps={{
          inputComponent: MaskedField as unknown as FunctionComponent<InputBaseComponentProps>,
          inputProps: {
            mask: '999-99-9999',
            placeholder: 'XXX-XX-XXXX',
          },
        }}
        required={required}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        fullWidth={fullWidth}
        variant="outlined"
      />
    );
  }

  if (identificationType === 'ein') {
    return (
      <ObscurableField
        key={`ein-${keyPrefix}`}
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        error={displayError}
        helperText={displayHelperText}
        obscurePattern={/\d/g}
        obscureReplacer={(match, offset) => {
          if (typeof offset === 'number' && offset < 6) {
            return '•';
          }
          return match;
        }}
        InputProps={{
          inputComponent: MaskedField as unknown as FunctionComponent<InputBaseComponentProps>,
          inputProps: {
            mask: '99-9999999',
            placeholder: 'XX-XXXXXXX',
          },
        }}
        required={required}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        fullWidth={fullWidth}
        variant="outlined"
      />
    );
  }

  // Alphanumeric-only fields: Foreign Tax ID, Foreign Passport, Foreign Entity Registration Number
  if (
    identificationType === 'foreignTaxId' ||
    identificationType === 'foreignPassport' ||
    identificationType === 'foreignEntityRegistrationNumber'
  ) {
    return (
      <AgentNetTextInput
        label={label}
        name={name}
        value={value}
        onChange={handleAlphanumericChange}
        onBlur={handleBlur}
        error={displayError}
        helperText={displayHelperText}
        required={required}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        fullWidth={fullWidth}
        variant="outlined"
      />
    );
  }

  // Default case for other identification types - also apply alphanumeric filtering
  return (
    <AgentNetTextInput
      label={label}
      name={name}
      value={value}
      onChange={handleAlphanumericChange}
      error={error}
      helperText={helperText}
      required={required}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      fullWidth={fullWidth}
      variant="outlined"
    />
  );
};
